/**
 * ATS Magic Links Tab Component
 * Generate and manage magic login links for users
 */

import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { Link2, Send, Copy, Loader2 } from 'lucide-react';

const ATSMagicLinksTab = ({ api, settings }) => {
    const [magicLinkEmail, setMagicLinkEmail] = useState('');
    const [magicLinkExpiry, setMagicLinkExpiry] = useState(24);
    const [generatingLink, setGeneratingLink] = useState(false);
    const [generatedLinks, setGeneratedLinks] = useState([]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const generateMagicLink = async () => {
        if (!magicLinkEmail.trim()) {
            toast.error('Please enter an email address');
            return;
        }
        
        setGeneratingLink(true);
        try {
            const response = await api.post('/ats/magic-link/generate', {
                email: magicLinkEmail,
                expires_in_hours: magicLinkExpiry
            });
            
            setGeneratedLinks(prev => [{
                email: response.data.user_email,
                link: response.data.magic_link,
                expires_at: response.data.expires_at,
                created_at: new Date().toISOString()
            }, ...prev.slice(0, 9)]);
            
            setMagicLinkEmail('');
            toast.success('Magic link generated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate magic link');
        } finally {
            setGeneratingLink(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="p-4 rounded-lg border space-y-4">
                <div>
                    <Label className="text-base font-semibold flex items-center gap-2">
                        <Link2 className="h-4 w-4" />
                        Generate Magic Login Link
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                        Create a one-time login link for a user (user must exist in the system)
                    </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <Label className="text-sm">User Email</Label>
                        <Input
                            type="email"
                            placeholder="user@company.com"
                            value={magicLinkEmail}
                            onChange={(e) => setMagicLinkEmail(e.target.value)}
                            data-testid="ats-magic-link-email-input"
                        />
                    </div>
                    <div>
                        <Label className="text-sm">Expires In (hours)</Label>
                        <Input
                            type="number"
                            min={1}
                            max={168}
                            value={magicLinkExpiry}
                            onChange={(e) => setMagicLinkExpiry(parseInt(e.target.value) || 24)}
                        />
                    </div>
                </div>
                
                <Button 
                    onClick={generateMagicLink} 
                    disabled={generatingLink || !settings?.enabled} 
                    data-testid="ats-generate-magic-link-btn"
                >
                    {generatingLink ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4 mr-2" />
                    )}
                    Generate Link
                </Button>
                
                {!settings?.enabled && (
                    <p className="text-sm text-amber-600">
                        Enable ATS integration in Settings tab first
                    </p>
                )}
            </div>

            {/* Generated Links History */}
            {generatedLinks.length > 0 && (
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Recent Links</Label>
                    <div className="space-y-2">
                        {generatedLinks.map((link, idx) => (
                            <div key={`magic-link-${link.email}-${idx}`} className="p-3 rounded-lg border bg-muted/30">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">{link.email}</span>
                                    <span className="text-xs text-muted-foreground">
                                        Expires: {new Date(link.expires_at).toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Input value={link.link} readOnly className="text-xs font-mono" />
                                    <Button size="icon" variant="ghost" onClick={() => copyToClipboard(link.link)}>
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ATSMagicLinksTab;
